import { useCallback, useEffect, useState } from 'react';

/**
 * The `beforeinstallprompt` event (Chromium only). Not in lib.dom yet, so we
 * type the two members we use.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type PromptOutcome = 'accepted' | 'dismissed' | 'unavailable';

function getIsStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const displayModeStandalone = window.matchMedia?.('(display-mode: standalone)').matches;
  // iOS Safari exposes navigator.standalone instead of the display-mode media query.
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(displayModeStandalone) || iosStandalone;
}

function getIsIos(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const iPhoneOrPad = /iphone|ipad|ipod/i.test(ua);
  // iPadOS 13+ reports a desktop Mac UA — fall back to touch detection.
  const iPadOsDesktop = /macintosh/i.test(ua) && 'ontouchend' in document;
  return iPhoneOrPad || iPadOsDesktop;
}

/**
 * Surfaces PWA install state for the current session.
 *
 * - `canPrompt` is true once Chromium has fired `beforeinstallprompt` (Android /
 *   desktop Chrome). Call `promptInstall()` to show the native install dialog.
 * - `isIos` is true on iOS Safari, which has no programmatic prompt — the UI
 *   must show manual "Add to Home Screen" instructions instead.
 * - `isStandalone` is true when the app is already running as an installed PWA,
 *   so the prompt UI should hide.
 */
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isStandalone, setIsStandalone] = useState(getIsStandalone);
  const [isIos] = useState(getIsIos);

  useEffect(() => {
    const onBeforeInstallPrompt = (e: Event) => {
      // Stop Chrome's default mini-infobar so we can present our own prompt.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    const mql = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = () => setIsStandalone(getIsStandalone());
    mql.addEventListener('change', onDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      mql.removeEventListener('change', onDisplayModeChange);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<PromptOutcome> => {
    if (!deferredPrompt) return 'unavailable';
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    // The event can only be used once.
    setDeferredPrompt(null);
    return choice.outcome;
  }, [deferredPrompt]);

  return {
    isStandalone,
    isIos,
    canPrompt: deferredPrompt !== null,
    promptInstall,
  };
}
